"use client"

import { useState } from "react"
import { Button } from "#/components/ui/button"
import { LoadingSpinner } from "#/components/ui/loading-spinner"
import { UserPlus, UserMinus, Clock } from "lucide-react"
import { toast } from "sonner"
import { useSendFollowRequest } from "#/hooks/use-follow"

export type FollowStatus = "not-following" | "pending" | "following"

interface FollowButtonProps {
  username: string
  initialStatus?: FollowStatus
  variant?: "default" | "outline" | "secondary" | "ghost"
  size?: "default" | "sm" | "lg"
  className?: string
}

export function FollowButton({
  username,
  initialStatus = "not-following",
  variant = "default",
  size = "default",
  className,
}: FollowButtonProps) {
  const [followStatus, setFollowStatus] = useState<FollowStatus>(initialStatus)
  const sendFollowRequestMutation = useSendFollowRequest()

  const handleFollow = async () => {
    if (followStatus === "following") {
      // TODO: Implement unfollow functionality
      toast.info("Unfollow functionality will be available soon")
      return
    }

    if (followStatus === "pending") {
      toast.info("Your follow request is already pending")
      return
    }

    try {
      await sendFollowRequestMutation.mutateAsync(username)
      setFollowStatus("pending")
      toast.success(`Follow request sent to @${username}`)
    } catch (error) {
      console.error(error)
      toast.error("Failed to send follow request")
    }
  }

  const getButtonContent = () => {
    if (sendFollowRequestMutation.isPending) {
      return <LoadingSpinner />
    }

    switch (followStatus) {
      case "not-following":
        return (
          <>
            <UserPlus className="h-4 w-4" />
            Follow
          </>
        )
      case "pending":
        return (
          <>
            <Clock className="h-4 w-4" />
            Pending
          </>
        )
      case "following":
        return (
          <>
            <UserMinus className="h-4 w-4" />
            Unfollow
          </>
        )
    }
  }

  const getButtonVariant = () => {
    switch (followStatus) {
      case "not-following":
        return variant
      case "pending":
        return "outline" as const
      case "following":
        return "secondary" as const
    }
  }

  return (
    <Button
      variant={getButtonVariant()}
      size={size}
      className={className}
      onClick={handleFollow}
      disabled={sendFollowRequestMutation.isPending}
    >
      {getButtonContent()}
    </Button>
  )
}
